import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import MapIconsService from '../../../../../../services/MapIconService/mapIcon.service';
import { ALLOWED_ICON_MIME_TYPES, validateIconFile } from '../../../../../../services/MapIconService/mapicon.constants';
import type { LibraryIcon } from '../constants';
import styles from './uploadIconPopover.module.scss';

type UploadStatus = 'idle' | 'error' | 'success';

const ICON_FILE_ACCEPT = ALLOWED_ICON_MIME_TYPES.join(',');

// 1px hairline outline, matching this feature's other custom stroke icons.
const PictureFrameIcon: React.FC = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden>
    <rect x="2" y="4" width="20" height="16" rx="1.5" />
    <circle cx="8" cy="10" r="1.5" />
    <path d="M3 17l5-5 4 4 3-3 6 6" />
  </svg>
);

interface UploadIconPopoverProps {
  onBack: () => void;
  // Receives the uploaded icon's library URL, which is what a statistic stores.
  onConfirm: (iconUrl: string) => void;
}

const UploadIconPopover: React.FC<UploadIconPopoverProps> = ({ onBack, onConfirm }) => {
  const queryClient = useQueryClient();
  const { getMapIcons, getPresignedUrl, uploadIcon } = MapIconsService();
  const { mutateAsync: getPresignedUrlAsync } = useMutation(getPresignedUrl());
  const { mutateAsync: uploadIconAsync } = useMutation(uploadIcon());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('File did not load.');
  const [isUploading, setIsUploading] = useState(false);

  // An object URL holds no base64 copy of the file; it is revoked when replaced or on close.
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const handleSelectFilesClick = () => fileInputRef.current?.click();

  const showError = (message: string) => {
    setErrorMessage(message);
    setStatus('error');
    setFile(null);
    setPreviewUrl(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    const validationError = validateIconFile(selected);
    if (validationError) return showError(validationError);
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
    setStatus('success');
  };

  // Same flow as Map Portal's icon upload: presign, direct S3 PUT, then register it in the library.
  const uploadToLibrary = async (selected: File) => {
    const { url, key } = await getPresignedUrlAsync({ fileName: selected.name, fileType: selected.type });
    const putResponse = await fetch(url, { method: 'PUT', body: selected, headers: { 'Content-Type': selected.type } });
    if (!putResponse.ok) throw new Error(`S3 upload failed (${putResponse.status})`);
    await uploadIconAsync({ key, name: selected.name });
    const icons: LibraryIcon[] = await queryClient.fetchQuery({ ...getMapIcons(), staleTime: 0 });
    const uploaded = icons.find((icon) => icon.s3Key === key);
    if (!uploaded) throw new Error('Uploaded icon is missing from the library');
    return uploaded.url;
  };

  const handleAddClick = async () => {
    if (!file) return;
    setIsUploading(true);
    try {
      onConfirm(await uploadToLibrary(file));
    } catch (error) {
      // Axios failures are already toasted by the icon service; the file is kept so Add can retry.
      setIsUploading(false);
      if (axios.isAxiosError(error)) return;
      setErrorMessage('Upload failed. Please try again.');
      setStatus('error');
    }
  };

  const handleAdd = () => void handleAddClick();

  const selectButtonClassName = `${styles.selectFilesButton} ${status === 'error' ? styles.selectFilesError : ''} ${
    status === 'success' ? styles.selectFilesSuccess : ''
  }`;

  return (
    <div className={styles.container}>
      <div className={styles.header}>UPLOAD IMAGE</div>
      <span className={styles.headerIcon}>
        <PictureFrameIcon />
      </span>
      <div className={styles.previewBox}>
        {previewUrl ? (
          <img src={previewUrl} alt="Selected icon preview" className={styles.previewImage} />
        ) : (
          <span className={styles.placeholderIcon}>
            <PictureFrameIcon />
          </span>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept={ICON_FILE_ACCEPT} onChange={handleFileChange} className={styles.hiddenInput} />
      <button type="button" className={selectButtonClassName} onClick={handleSelectFilesClick} disabled={isUploading}>
        {status === 'success' ? 'Success!' : 'Select Files Locally'}
      </button>
      {status === 'error' && (
        <div className={styles.errorRow}>
          <WarningOutlined />
          <span>Error! {errorMessage}</span>
        </div>
      )}
      <div className={styles.footer}>
        <Button className={styles.backButton} onClick={onBack} disabled={isUploading}>
          Back
        </Button>
        <Button className={styles.addButton} onClick={handleAdd} disabled={!file} loading={isUploading}>
          Add
        </Button>
      </div>
    </div>
  );
};

export default UploadIconPopover;
